import { useState, useEffect } from "react";
import { Shield, ChevronDown, ChevronUp } from "lucide-react";

const CONSENT_KEY = "justfit_cookie_consent";

function ensureGtagBase() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      wait_for_update: 500,
    });
    window.gtag("js", new Date());
  }
}

function loadGoogleAnalytics() {
  ensureGtagBase();
  window.gtag("consent", "update", { analytics_storage: "granted" });
  window.gtag("config", "G-Q6PBMVZE14");
  if (document.getElementById("ga-script")) return;
  const s = document.createElement("script");
  s.id = "ga-script";
  s.async = true;
  s.src = "https://www.googletagmanager.com/gtag/js?id=G-Q6PBMVZE14";
  document.head.appendChild(s);
}

function loadFacebookPixel() {
  if (window.fbq) return;
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = true; n.version = "2.0";
    n.queue = [];
    t = b.createElement(e); t.async = true; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", "1608633349211532");
  window.fbq("track", "PageView");
}

function loadGoogleAds() {
  ensureGtagBase();
  window.gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
  window.gtag("config", "AW-833084921");
  if (document.getElementById("gads-script")) return;
  const s = document.createElement("script");
  s.id = "gads-script";
  s.async = true;
  s.src = "https://www.googletagmanager.com/gtag/js?id=AW-833084921";
  document.head.appendChild(s);
}

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: true,
    marketing: true,
  });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.analytics) loadGoogleAnalytics();
      if (parsed.marketing) {
        loadFacebookPixel();
        loadGoogleAds();
      }
    } else {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const consent = { analytics: true, marketing: true };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    loadGoogleAnalytics();
    loadFacebookPixel();
    loadGoogleAds();
    setVisible(false);
  };

  const rejectAll = () => {
    const consent = { analytics: false, marketing: false };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);
  };

  const savePreferences = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences));
    if (preferences.analytics) loadGoogleAnalytics();
    if (preferences.marketing) {
      loadFacebookPixel();
      loadGoogleAds();
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto bg-[#1A1A1A] border border-white/10 rounded-2xl shadow-2xl p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-10 h-10 bg-[#FED601]/10 rounded-full items-center justify-center flex-shrink-0 mt-0.5">
            <Shield size={20} className="text-[#FED601]" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-base mb-1">
              Este site utiliza cookies
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Utilizamos cookies para melhorar a tua experiência, analisar o tráfego e personalizar conteúdos e anúncios.
              Consulta a nossa{" "}
              <a
                href="https://www.justfitcenter.pt/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FED601] underline hover:no-underline"
              >
                Política de Privacidade
              </a>.
            </p>

            {/* Toggle details */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-gray-400 text-xs mt-2 hover:text-gray-300 transition-colors"
              data-testid="cookie-toggle-details"
            >
              Personalizar preferências
              {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {showDetails && (
              <div className="mt-3 space-y-2.5 border-t border-white/10 pt-3">
                <label className="flex items-center gap-3 text-sm">
                  <input type="checkbox" checked disabled className="accent-[#FED601] w-4 h-4" />
                  <span className="text-gray-300">Necessários</span>
                  <span className="text-gray-600 text-xs">(sempre ativos)</span>
                </label>
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences((p) => ({ ...p, analytics: e.target.checked }))}
                    className="accent-[#FED601] w-4 h-4"
                    data-testid="cookie-analytics-toggle"
                  />
                  <span className="text-gray-300">Analíticos</span>
                  <span className="text-gray-600 text-xs">(Google Analytics)</span>
                </label>
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences((p) => ({ ...p, marketing: e.target.checked }))}
                    className="accent-[#FED601] w-4 h-4"
                    data-testid="cookie-marketing-toggle"
                  />
                  <span className="text-gray-300">Marketing</span>
                  <span className="text-gray-600 text-xs">(Facebook Pixel, Google Ads)</span>
                </label>
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-wrap gap-3 mt-4">
              <button
                onClick={acceptAll}
                className="bg-[#FED601] text-black font-bold text-sm px-6 py-2.5 rounded-lg hover:bg-[#E0BC00] transition-colors"
                data-testid="cookie-accept-all"
              >
                Aceitar Todos
              </button>
              {showDetails ? (
                <button
                  onClick={savePreferences}
                  className="bg-white/10 text-white font-medium text-sm px-6 py-2.5 rounded-lg hover:bg-white/20 transition-colors"
                  data-testid="cookie-save-preferences"
                >
                  Guardar Preferências
                </button>
              ) : (
                <button
                  onClick={rejectAll}
                  className="bg-white/10 text-white font-medium text-sm px-6 py-2.5 rounded-lg hover:bg-white/20 transition-colors"
                  data-testid="cookie-reject-all"
                >
                  Rejeitar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
